import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Map, fromJS } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getChanged, isDeepChanged, getIn, unwrap } from '../../utils/ChangeSpy';
import getOptionValue from '../../utils/getOptionValue';
import * as TARGET from '../../actions/company/companyTarget';
import * as BUYER from '../../actions/company/buyer/marketing';
import config from '../../config';
import { getBirthYear } from '../../utils/yearAgeConverter';
import AutoComplete from '../AutoComplete';
import getPhoneChanged, {
  checkPhoneIfNeeded,
  formatPhoneIfNeeded,
  checkExtensionIfNeeded,
  checkCountryIfNeeded,
} from '../services/phoneRules';
import stripOffChars from '../../utils/stripOffChars';
import { checkNameChangable, checkFirstName, checkLastName, parseFullName } from '../services/nameRules';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

const prefixes = config.values.getIn(['contact', 'prefixes']);
const suffixes = config.values.getIn(['contact', 'suffixes']);

const handleGetValue = suggestion => suggestion?.value ?? '';

const trimDoubleSpaces = (name = '') => name.replace(/\s{2,}/g, ' ');

const AddCompanyContactPopupContainer = ({
  children,
  contact,
  contactInfo,
  companyId,
  pathPrefix,
  phoneCodes,
  loading,
  verifyEmail,
  handleUpdateCompany,
  saveCompanyContact,
  revertUpdate,
  findTitles,
  loadCountryPhoneCodes,
}) => {
  const context = usePopupContext();
  const [errors, setErrors] = useState({
    fullName: '',
    firstName: '',
    lastName: '',
    country: '',
    extension: '',
    unformattedPhone: '',
  });

  const changable = useRef({
    fullName: false,
    firstName: false,
    lastName: false,
  });

  const contactRef = useRef(contact);

  contactRef.current = contact;

  useEffect(() => {
    if (!phoneCodes.size) {
      loadCountryPhoneCodes();
    }
  }, []);

  useEffect(() => {
    context.onClosePopup(() => {
      const field = pathPrefix.slice(0, -1);

      revertUpdate({ field });
    });
  }, []);

  const setError = useCallback(
    ({ unformattedPhone, firstName, lastName, fullName, extension, country, lastSuffixesName, firstInitialsName }) => {
      setErrors(prev => {
        const next = { ...prev };

        if (unformattedPhone === true) {
          next.unformattedPhone =
            'Phone must be a valid North American area code and format (###-###-####) or +[numbers, spaces and dashes] if international';
        } else if (unformattedPhone === false) {
          next.unformattedPhone = '';
        }

        if (extension === true) {
          next.extension = 'The extension field is required when type is Extension.';
        } else if (extension === false) {
          next.extension = '';
        }

        if (country === true) {
          next.country = 'The country field is required when type is International.';
        } else if (country === false) {
          next.country = '';
        }

        if (firstName === true) {
          next.firstName = 'The field is required.';
        } else if (firstName === false) {
          next.firstName = '';
        }

        if (firstInitialsName === true) {
          next.firstName = 'Enter only the first name with no initials, etc.';
        } else if (firstInitialsName === false) {
          next.firstName = firstName ? next.firstName : '';
        }

        if (lastName === true) {
          next.lastName = 'The field is required.';
        } else if (lastName === false) {
          next.lastName = '';
        }

        if (lastSuffixesName === true) {
          next.lastName = 'Enter only the last name with no initials, suffixes, etc.';
        } else if (lastSuffixesName === false) {
          next.lastName = lastName ? next.lastName : '';
        }

        if (fullName === true) {
          next.fullName = 'The field is required.';
        } else if (fullName === false) {
          next.fullName = '';
        }

        return next;
      });
    },
    [],
  );

  const clearAllError = useCallback(() => {
    setError({
      unformattedPhone: false,
      extension: false,
      country: false,
      firstName: false,
      firstInitialsName: false,
      lastSuffixesName: false,
      lastName: false,
      fullName: false,
    });
  }, [setError]);

  const handleErrorClose = useCallback(() => {
    clearAllError();
  }, [clearAllError]);

  const updateFirstName = useCallback(
    name => {
      const contact = contactRef.current;
      const trimmedName = trimDoubleSpaces(name);
      const firstName = unwrap(contact.get('firstName') || '');
      let fullName = unwrap(contact.get('fullName') || '');

      fullName = fullName.indexOf(firstName) > -1 ? fullName.substring(firstName.length) : fullName;

      if (changable.current.firstName) {
        handleUpdateCompany({ name: `${pathPrefix}firstName`, value: trimmedName });
      }

      if (changable.current.fullName) {
        handleUpdateCompany({
          name: `${pathPrefix}fullName`,
          value: trimDoubleSpaces(`${trimmedName} ${fullName}`),
        });
      }
    },
    [pathPrefix, handleUpdateCompany],
  );

  const updateLastName = useCallback(
    name => {
      const contact = contactRef.current;
      const trimmedName = trimDoubleSpaces(name);
      const lastName = unwrap(contact.get('lastName') || '');
      let fullName = unwrap(contact.get('fullName') || '');

      fullName = fullName.lastIndexOf(lastName) > -1 ? fullName.substring(0, fullName.lastIndexOf(lastName)) : fullName;

      if (changable.current.lastName) {
        handleUpdateCompany({ name: `${pathPrefix}lastName`, value: trimmedName });
      }

      if (changable.current.fullName) {
        handleUpdateCompany({
          name: `${pathPrefix}fullName`,
          value: trimDoubleSpaces(`${fullName} ${trimmedName}`),
        });
      }
    },
    [pathPrefix, handleUpdateCompany],
  );

  const updateFullName = useCallback(
    name => {
      const nameParts = parseFullName(name);

      if (changable.current.firstName) {
        handleUpdateCompany({ name: `${pathPrefix}firstName`, value: nameParts[0] });
      }

      if (changable.current.lastName) {
        handleUpdateCompany({ name: `${pathPrefix}lastName`, value: nameParts[1] });
      }

      if (changable.current.fullName) {
        handleUpdateCompany({ name: `${pathPrefix}fullName`, value: nameParts[2] });
      }
    },
    [pathPrefix, handleUpdateCompany],
  );

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      switch (name) {
        case 'country': {
          const index = phoneCodes.findIndex(p => p.get('code') === value);

          if (index > -1) {
            handleUpdateCompany({
              name: `${pathPrefix}code`,
              value: phoneCodes.getIn([index, 'dialCode']),
            });
          }
          break;
        }

        case 'firstName':
          updateFirstName(value);

          return;

        case 'lastName':
          updateLastName(value);

          return;

        case 'fullName':
          updateFullName(value);

          return;

        default:
          break;
      }

      return handleUpdateCompany({
        name: `${pathPrefix}${name}`,
        value: name === 'extension' ? stripOffChars(value, '', /[^\d]/gi) : value,
      });
    },
    [phoneCodes, pathPrefix, handleUpdateCompany, updateFirstName, updateLastName, updateFullName],
  );

  const handleUndo = useCallback(() => {
    handleUpdateCompany({ name: `${pathPrefix}type`, value: 'Direct' });
  }, [handleUpdateCompany, pathPrefix]);

  const handlePhoneBlur = useCallback(
    ({ target: { value } }) => {
      const type = unwrap(contactRef.current.get('type', ''));

      if (checkPhoneIfNeeded(type, value)) {
        handleChange({
          target: {
            name: 'unformattedPhone',
            value: formatPhoneIfNeeded(type, value),
          },
        });
        setError({ unformattedPhone: false });
      } else {
        setError({ unformattedPhone: true });
      }
    },
    [handleChange, setError],
  );

  const handleExtensionBlur = useCallback(
    ({ target: { value } }) => {
      const type = unwrap(contactRef.current.get('type', ''));

      if (checkExtensionIfNeeded(type, value)) {
        setError({ extension: false });
      } else {
        setError({ extension: true });
      }
    },
    [setError],
  );

  const handleCountryBlur = useCallback(
    ({ target: { value } }) => {
      const type = unwrap(contactRef.current.get('type', ''));

      if (checkCountryIfNeeded(type, value)) {
        setError({ country: false });
      } else {
        setError({ country: true });
      }
    },
    [setError],
  );

  const handleBlur = useCallback(
    event => {
      const {
        target: { name, value },
      } = event;

      if (name === 'unformattedPhone') {
        handlePhoneBlur(event);
      } else if (name === 'extension') {
        handleExtensionBlur(event);
      } else if (name === 'country') {
        handleCountryBlur(event);
      } else if (name === 'firstName' && !checkFirstName(value)) {
        setError({ firstInitialsName: true });
        handleUpdateCompany({ name: `${pathPrefix}firstName`, value: '' });
      } else if (name === 'lastName' && !checkLastName(value)) {
        setError({ lastSuffixesName: true });
      }
    },
    [handlePhoneBlur, handleExtensionBlur, handleCountryBlur, setError, handleUpdateCompany, pathPrefix],
  );

  const handleFocus = useCallback(({ target: { name } }) => {
    if (['firstName', 'lastName', 'fullName'].indexOf(name) > -1) {
      changable.current = checkNameChangable(name);
    }
  }, []);

  const checkValidity = useCallback(() => {
    const contact = contactRef.current;
    const validationErrors = {
      unformattedPhone: false,
      extension: false,
      country: false,
      fullName: false,
      firstName: false,
      lastName: false,
    };
    const required = ['fullName', 'firstName', 'lastName'];
    const phone = unwrap(contact.get('unformattedPhone', ''));
    const extension = unwrap(contact.get('extension', ''));
    const country = unwrap(contact.get('country', ''));
    const type = unwrap(contact.get('type', ''));

    required.forEach(field => {
      validationErrors[field] = unwrap(contact.get(field)) === '';
    });

    if (phone !== '') {
      validationErrors.unformattedPhone = !checkPhoneIfNeeded(type, phone);
      validationErrors.extension = !checkExtensionIfNeeded(type, extension);
      validationErrors.country = !checkCountryIfNeeded(type, country);
    }

    return validationErrors;
  }, []);

  /**
   * Api request needs ['advTitle', 'advWeb', 'company'] parameters stay outside of pivot object.
   */
  const moveOut = useCallback(body => {
    if (body.pivot) {
      ['advTitle', 'advWeb', 'company'].forEach(key => {
        if (body.pivot[key]) {
          body[key] = body.pivot[key];
          delete body.pivot[key];
        }

        return true;
      });
    }
  }, []);

  const handleSave = useCallback(() => {
    const validationErrors = checkValidity();
    const isValid = !Object.keys(validationErrors).some(key => validationErrors[key] === true);

    if (isValid) {
      const contact = contactRef.current;
      const contactId = contact.get('id');
      const phone = unwrap(contact.get('unformattedPhone', ''));
      const saved = getChanged(contact);

      saved.nick = unwrap(contact.get('nick'));
      saved.pronounce = unwrap(contact.get('pronounce'));
      saved.advTitle = unwrap(contact.getIn(['pivot', 'advTitle']));
      saved.advWeb = unwrap(contact.getIn(['pivot', 'advWeb']));
      saved.company = unwrap(contact.getIn(['pivot', 'company']));

      const pivot = saved.pivot || {};
      const prefix = getOptionValue(contact.get('prefix'));
      const suffix = getOptionValue(contact.get('suffix'));
      const isVerify = verifyEmail.get('status') === 'valid';
      let body = { ...pivot, ...saved, prefix, suffix, ...(isVerify && { isVerify }) };

      if (phone === '') {
        delete body.unformattedPhone;
        delete body.type;
        delete body.extension;
        delete body.country;
      } else {
        body = { ...body, ...getPhoneChanged(contact) };
      }

      if ('age' in body) {
        body.birthYear = getBirthYear(body.age);
      }
      moveOut(body);
      saveCompanyContact({ companyId, contactId, isNew: true, body, saved }, () => {
        context.closePopup();
      });

      return;
    }
    setError(validationErrors);
  }, [checkValidity, contact, verifyEmail, companyId, moveOut, saveCompanyContact, context, setError]);

  const handleFetchTitles = useCallback(
    ({ value }) => {
      findTitles({ filter: value, prefix: 'newContact' });
    },
    [findTitles],
  );

  const handleSelectTitle = useCallback(
    (event, { suggestion }) => {
      handleUpdateCompany({ name: `${pathPrefix}pivot.title`, value: suggestion.text });
    },
    [handleUpdateCompany, pathPrefix],
  );

  const handleUpdateTitle = useCallback(
    param => {
      if ('text' in param) {
        handleSelectTitle(null, { suggestion: { text: param.text } });
      }

      if ('suggestions' in param) {
        const name = 'suggests';
        const value = param.suggestions;

        handleUpdateCompany({ name: `${pathPrefix}${name}`, value });
      }
    },
    [handleSelectTitle, handleUpdateCompany, pathPrefix],
  );

  const handleChangeSuggestion = useCallback(
    object => {
      if (typeof object.value === 'string') {
        const { value } = object;
        const nextValue = suffixes.find(
          suffix => suffix.get('name') === value,
          null,
          Map({ name: value || '', value: value || '' }),
        );

        handleUpdateCompany({ name: `${pathPrefix}suffix`, value: nextValue });
      }
    },
    [suffixes, handleUpdateCompany, pathPrefix],
  );

  const handleFindEmail = useCallback(() => {
    context.openPopup('EmailFinderPopup', {
      contact,
      companyId,
      pathPrefix,
      phoneCodes,
      loading,
      verifyEmail,
      handleUpdateCompany,
      saveCompanyContact,
      revertUpdate,
      findTitles,
      loadCountryPhoneCodes,
      reopenPopupName: 'AddCompanyContactPopup',
    });
  }, [
    context,
    contact,
    companyId,
    pathPrefix,
    phoneCodes,
    loading,
    verifyEmail,
    handleUpdateCompany,
    saveCompanyContact,
    revertUpdate,
    findTitles,
    loadCountryPhoneCodes,
  ]);

  const titleValue = contact.getIn(['pivot', 'title'], '');
  const titleText = unwrap(titleValue);
  const titleProps = {
    onFetch: handleFetchTitles,
    suggestions: contact.getIn(['suggestions']),
    onUpdate: handleUpdateTitle,
    getValue: handleGetValue,
    onSelect: handleSelectTitle,
    value: titleValue,
    showExtraInfo: titleText && titleText.toUpperCase() === 'ADVISOR',
    loading,
  };

  const suffixValue = getIn(contactInfo, 'suffix').get('name');
  const regExp = new RegExp(String(suffixValue).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1'), 'i');
  const suffixClassName = classNames('form-control input-sm', {
    changed: isDeepChanged(contactInfo.get('suffix')),
  });
  const suffixSuggestions = suffixes
    .filter(suffix => regExp.test(suffix.get('name')))
    .map(suffix => ({ id: suffix.get('value'), text: suffix.get('name') }));

  const suffixSuggest = (
    <AutoComplete
      idValue={suffixValue}
      inputProps={{ className: suffixClassName }}
      loading={false}
      name="suffix"
      onChange={handleChangeSuggestion}
      onFind={args => {
        setTimeout(
          () =>
            args.afterSuccess({
              response: {
                data: suffixes
                  .filter(suffix => regExp.test(suffix.get('name')))
                  .map(suffix => ({ id: suffix.get('value'), text: suffix.get('name') })),
              },
            }),
          0,
        );
      }}
      suggestions={suffixSuggestions}
      suggestionsName="suffixes"
      value={suffixValue}
      suggestIfEmpty
    />
  );

  return (
    <PrettyErrorProvider inputErrors={fromJS(errors)} onErrorClose={handleErrorClose}>
      <>
        <Popups.AddCompanyContactPopup
          contact={contact}
          onBlur={handleBlur}
          onChange={handleChange}
          onFindEmail={handleFindEmail}
          onFocus={handleFocus}
          onSave={handleSave}
          onUndo={handleUndo}
          phoneCodes={phoneCodes}
          prefixes={prefixes}
          suffixes={suffixes}
          suffixSuggest={suffixSuggest}
          titleProps={titleProps}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';
  const root = isBuyer ? state.targetCompany.buyer.marketing : state.targetCompany.target;
  const contact = root.get('newContact');
  const phoneCodes = root.get('phoneCodes');

  return {
    companyId: state.targetCompany.info.getIn(['info', 'id']),
    inputErrors: contact.get('inputErrors', Map()),
    pathPrefix: 'newContact.',
    prefixes,
    suffixes,
    contact,
    contactInfo: contact,
    loading: contact.get('loading'),
    phoneCodes,
    verifyEmail: state.verifyEmail,
  };
}

function mapDispatchToProps(dispatch, props) {
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';

  if (isBuyer) {
    const {
      handleUpdateCompany,
      saveCompanyContact,
      revertUpdate,
      closeValidationError,
      addValidationError,
      findTitles,
    } = BUYER;

    return bindActionCreators(
      {
        handleUpdateCompany,
        saveCompanyContact,
        revertUpdate,
        closeValidationError,
        findTitles,
        addValidationError,
        loadCountryPhoneCodes: TARGET.loadCountryPhoneCodes,
      },
      dispatch,
    );
  }

  const {
    handleUpdateCompany,
    saveCompanyContact,
    revertUpdate,
    closeValidationError,
    addValidationError,
    findTitles,
    loadCountryPhoneCodes,
  } = TARGET;

  return bindActionCreators(
    {
      handleUpdateCompany,
      saveCompanyContact,
      revertUpdate,
      closeValidationError,
      findTitles,
      addValidationError,
      loadCountryPhoneCodes,
    },
    dispatch,
  );
}

export { AddCompanyContactPopupContainer };

const Connected = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(AddCompanyContactPopupContainer));

export default Connected;
